import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, BRAND, productImage } from "../lib/brand";
import { LogOut, Upload, Plus, Trash2, Mail, DollarSign, Package, ShoppingBag, Loader2, Save } from "lucide-react";
import BrandMark from "../components/BrandMark";
import { toast } from "sonner";

const TOKEN_KEY = "gra_admin_token";

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
function clearToken() { localStorage.removeItem(TOKEN_KEY); }

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      onLogin();
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md p-10 bg-[#09120C] border border-[#1A2B20]">
        <BrandMark variant="knot" className="h-12 w-12 mb-6 block" />
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Studio</div>
        <h1 className="font-serif-display text-3xl text-white mb-8">Admin access</h1>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Password</label>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-6"
          autoFocus
        />
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
        >
          {loading ? "…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="p-6 bg-[#09120C] border border-[#1A2B20]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{label}</span>
        <Icon className="w-4 h-4 text-[#279153]" />
      </div>
      <div className="font-serif-display text-3xl text-white">{value}</div>
    </div>
  );
}

function UploadField({ label, accept, kind, onUploaded, currentPath, testid }) {
  const [uploading, setUploading] = useState(false);
  const onChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onUploaded(res.data.path);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">{label}</label>
      <label className="cursor-pointer block">
        <input type="file" accept={accept} onChange={onChange} className="hidden" data-testid={testid} />
        <div className="px-4 py-3 bg-[#050605] border border-dashed border-[#1A2B20] hover:border-[#279153] text-sm text-[#95A89C] flex items-center gap-3">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          <span>{currentPath ? "Replace file" : "Choose file"}</span>
          {currentPath && <span className="text-[10px] text-[#5C7064] truncate ml-auto">✓ uploaded</span>}
        </div>
      </label>
    </div>
  );
}

function ProductForm({ albums, onCreated }) {
  const [form, setForm] = useState({ type: "music", title: "", description: "", price: "", cover_path: null, audio_path: null, album_id: "", track_number: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) {
      toast.error("Title and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        cover_path: form.cover_path,
        audio_path: form.audio_path,
      };
      if (form.type === "music") {
        if (form.album_id) payload.album_id = form.album_id;
        if (form.track_number) payload.track_number = parseInt(form.track_number, 10);
      }
      await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
      toast.success("Piece released");
      setForm({ type: "music", title: "", description: "", price: "", cover_path: null, audio_path: null, album_id: "", track_number: "" });
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="product-create-form" className="p-6 bg-[#09120C] border border-[#1A2B20] space-y-4">
      <h3 className="font-serif-display text-2xl text-white mb-2">New release</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Type</label>
          <select
            data-testid="product-type-select"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
          >
            <option value="music">Music</option>
            <option value="photography">Photography</option>
            <option value="drawing">Visual Artwork</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Price (USD)</label>
          <input
            data-testid="product-price-input"
            type="number"
            step="0.01"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Title</label>
        <input
          data-testid="product-title-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
        />
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Description</label>
        <textarea
          data-testid="product-description-input"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
        />
      </div>

      {form.type === "music" && (
        <div className="grid sm:grid-cols-3 gap-4 p-3 bg-[#050605] border border-dashed border-[#1A2B20]">
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Album (optional)</label>
            <select
              data-testid="product-album-select"
              value={form.album_id}
              onChange={(e) => setForm({ ...form, album_id: e.target.value })}
              className="w-full px-3 py-2.5 bg-[#09120C] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
            >
              <option value="">— Single (no album)</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Track #</label>
            <input
              data-testid="product-track-number"
              type="number"
              min="1"
              value={form.track_number}
              onChange={(e) => setForm({ ...form, track_number: e.target.value })}
              className="w-full px-3 py-2.5 bg-[#09120C] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
            />
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <UploadField
          label="Cover image"
          accept="image/*"
          kind="image"
          currentPath={form.cover_path}
          onUploaded={(p) => setForm((f) => ({ ...f, cover_path: p }))}
          testid="upload-cover"
        />
        {form.type === "music" && (
          <UploadField
            label="Audio track (MP3, WAV)"
            accept="audio/*"
            kind="audio"
            currentPath={form.audio_path}
            onUploaded={(p) => setForm((f) => ({ ...f, audio_path: p }))}
            testid="upload-audio"
          />
        )}
      </div>
      <button
        type="submit"
        data-testid="product-create-submit"
        disabled={saving}
        className="w-full px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Release piece
      </button>
    </form>
  );
}

function ProductRow({ product, albums = [], onChanged }) {
  const [price, setPrice] = useState(product.price);
  const [saving, setSaving] = useState(false);

  const savePrice = async () => {
    if (parseFloat(price) === Number(product.price)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { price: parseFloat(price) }, { headers: authHeaders() });
      toast.success("Price updated");
      onChanged();
    } catch { toast.error("Could not update price"); }
    finally { setSaving(false); }
  };

  const changeAlbum = async (album_id) => {
    try {
      await axios.put(
        `${API}/admin/products/${product.id}`,
        { album_id: album_id || null },
        { headers: authHeaders() },
      );
      toast.success(album_id ? "Added to album" : "Removed from album");
      onChanged();
    } catch { toast.error("Could not update album"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${product.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.id}`, { headers: authHeaders() });
      toast.success("Removed");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid={`admin-product-${product.id}`} className="p-3 bg-[#050605] border border-[#1A2B20] space-y-2">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-black flex-shrink-0 overflow-hidden">
          <img src={productImage(product)} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{product.type}</div>
          <div className="text-white truncate">{product.title}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#5C7064]">$</span>
          <input
            data-testid={`admin-price-${product.id}`}
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-24 px-2 py-2 bg-[#09120C] border border-[#1A2B20] text-white text-sm text-right"
          />
          <button
            data-testid={`admin-price-save-${product.id}`}
            onClick={savePrice}
            disabled={saving}
            className="p-2 text-[#95A89C] hover:text-[#279153]"
          >
            <Save className="w-4 h-4" />
          </button>
          <button data-testid={`admin-delete-${product.id}`} onClick={remove} className="p-2 text-[#95A89C] hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {product.type === "music" && (
        <div className="flex items-center gap-2 pl-[68px]">
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">Album:</label>
          <select
            data-testid={`admin-album-select-${product.id}`}
            defaultValue={product.album_id || ""}
            onChange={(e) => changeAlbum(e.target.value)}
            className="flex-1 px-2 py-1.5 bg-[#09120C] border border-[#1A2B20] text-white text-xs focus:outline-none focus:border-[#279153]"
          >
            <option value="">— Single</option>
            {albums.map((a) => (
              <option key={a.id} value={a.id}>{a.title}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function SalesTable({ sales, onResend }) {
  if (sales.length === 0) {
    return <div className="p-8 text-center text-sm text-[#95A89C] border border-dashed border-[#1A2B20]">No sales yet.</div>;
  }
  return (
    <div className="space-y-2">
      {sales.map((s) => (
        <div key={s.session_id} data-testid={`sale-row-${s.session_id}`} className="flex items-center gap-4 p-4 bg-[#050605] border border-[#1A2B20]">
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm truncate">{s.customer_email}</div>
            <div className="text-xs text-[#5C7064] truncate">{s.item_titles}</div>
          </div>
          <div className="text-right">
            <div className="font-serif-display text-white">${Number(s.amount).toFixed(2)}</div>
            <div className={`text-[10px] tracking-[0.3em] uppercase ${
              s.payment_status === "paid" ? "text-[#279153]" : s.payment_status === "pending" ? "text-yellow-500" : "text-red-400"
            }`}>{s.payment_status}</div>
          </div>
          {s.payment_status === "paid" && (
            <button
              data-testid={`resend-thanks-${s.session_id}`}
              onClick={() => onResend(s.session_id)}
              className="p-2 text-[#95A89C] hover:text-[#279153]"
              title="Resend thank-you email"
            >
              <Mail className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function AlbumTrackList({ album, onChanged }) {
  const [tracks, setTracks] = useState(album.tracks || []);
  const dragId = useRef(null);

  useEffect(() => { setTracks(album.tracks || []); }, [album]);

  const onDragStart = (e, id) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId.current;
    dragId.current = null;
    if (!fromId || fromId === targetId) return;
    const oldIdx = tracks.findIndex((t) => t.id === fromId);
    const newIdx = tracks.findIndex((t) => t.id === targetId);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = [...tracks];
    const [moved] = next.splice(oldIdx, 1);
    next.splice(newIdx, 0, moved);
    setTracks(next);
    try {
      await axios.put(
        `${API}/admin/albums/${album.id}/reorder`,
        { track_ids: next.map((t) => t.id) },
        { headers: authHeaders() },
      );
      toast.success("Order saved");
      onChanged();
    } catch {
      toast.error("Could not reorder");
      setTracks(album.tracks || []);
    }
  };

  if (tracks.length === 0) {
    return <div className="text-xs text-[#5C7064] px-2 py-2">No tracks in this album. Add tracks via the Catalogue tab.</div>;
  }
  return (
    <div className="space-y-1.5 pl-2" data-testid={`album-tracks-${album.id}`}>
      {tracks.map((t, i) => (
        <div
          key={t.id}
          draggable
          onDragStart={(e) => onDragStart(e, t.id)}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, t.id)}
          data-testid={`album-track-${t.id}`}
          className="grid grid-cols-[24px_28px_1fr_auto] items-center gap-2 p-2 bg-[#09120C] border border-[#1A2B20] cursor-move hover:border-[#279153]"
        >
          <span className="text-[#5C7064] text-xs">⋮⋮</span>
          <span className="text-[#5C7064] text-xs font-mono">{i + 1}</span>
          <span className="text-white text-sm truncate">{t.title}</span>
          <span className="text-xs text-[#95A89C]">${Number(t.price).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function AlbumsPanel({ albums, onChanged }) {
  const [form, setForm] = useState({ title: "", description: "", cover_path: null, album_price: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        cover_path: form.cover_path,
      };
      if (form.album_price) payload.album_price = parseFloat(form.album_price);
      await axios.post(`${API}/admin/albums`, payload, { headers: authHeaders() });
      toast.success("Album created");
      setForm({ title: "", description: "", cover_path: null, album_price: "" });
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create album"); }
    finally { setSaving(false); }
  };

  const removeAlbum = async (id) => {
    if (!window.confirm("Delete this album? Its tracks will remain as singles.")) return;
    try {
      await axios.delete(`${API}/admin/albums/${id}`, { headers: authHeaders() });
      toast.success("Album removed");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  const savePrice = async (album, newPrice) => {
    try {
      await axios.put(`${API}/admin/albums/${album.id}`, { album_price: parseFloat(newPrice) }, { headers: authHeaders() });
      toast.success("Album price updated");
      setEditing(null);
      onChanged();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div data-testid="admin-albums" className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="p-6 bg-[#09120C] border border-[#1A2B20] space-y-4 self-start">
        <h3 className="font-serif-display text-2xl text-white mb-2">New album</h3>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Title</label>
          <input
            data-testid="album-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Description</label>
          <textarea
            data-testid="album-description-input"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Whole album price (optional)</label>
            <input
              data-testid="album-price-input"
              type="number"
              step="0.01"
              min="0"
              placeholder="leave blank for tracks only"
              value={form.album_price}
              onChange={(e) => setForm({ ...form, album_price: e.target.value })}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:outline-none focus:border-[#279153]"
            />
          </div>
          <UploadField
            label="Album cover"
            accept="image/*"
            kind="image"
            currentPath={form.cover_path}
            onUploaded={(p) => setForm((f) => ({ ...f, cover_path: p }))}
            testid="upload-album-cover"
          />
        </div>
        <button
          type="submit"
          data-testid="album-create-submit"
          disabled={saving}
          className="w-full px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create album
        </button>
      </form>

      <div>
        <h3 className="font-serif-display text-2xl text-white mb-4">Albums</h3>
        <div className="space-y-3">
          {albums.length === 0 && <div className="text-sm text-[#95A89C]">No albums yet.</div>}
          {albums.map((a) => (
            <div key={a.id} data-testid={`admin-album-${a.id}`} className="p-3 bg-[#050605] border border-[#1A2B20]">
              <div className="flex items-center gap-3">
                <button
                  data-testid={`album-expand-${a.id}`}
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="w-14 h-14 bg-black flex-shrink-0 overflow-hidden focus:outline-none"
                  title={expanded === a.id ? "Collapse" : "Expand tracks"}
                >
                  {a.cover_path ? <img src={`${API}/files/${a.cover_path}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#5C7064] text-xs">no cover</div>}
                </button>
                <button
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-white text-sm truncate">{a.title}</div>
                  <div className="text-xs text-[#5C7064]">
                    {a.track_count} track{a.track_count === 1 ? "" : "s"}
                    {expanded === a.id ? " · click to collapse" : " · click to reorder"}
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  {editing === a.id ? (
                    <input
                      data-testid={`album-price-edit-${a.id}`}
                      type="number"
                      step="0.01"
                      defaultValue={a.album_price || ""}
                      onBlur={(e) => savePrice(a, e.target.value)}
                      autoFocus
                      className="w-20 px-2 py-1.5 bg-[#09120C] border border-[#279153] text-white text-xs text-right"
                    />
                  ) : (
                    <button
                      data-testid={`album-price-${a.id}`}
                      onClick={() => setEditing(a.id)}
                      className="text-xs text-[#95A89C] hover:text-[#279153]"
                    >
                      {a.album_price != null ? `$${Number(a.album_price).toFixed(2)}` : "set price"}
                    </button>
                  )}
                  <button onClick={() => removeAlbum(a.id)} className="p-2 text-[#95A89C] hover:text-red-400" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {expanded === a.id && (
                <div className="mt-3 pt-3 border-t border-[#1A2B20]">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Drag to reorder</div>
                  <AlbumTrackList album={a} onChanged={onChanged} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InboxPanel() {
  const [items, setItems] = useState([]);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    try {
      const [inb, ct] = await Promise.all([
        axios.get(`${API}/admin/inbox`, { params: { archived: showArchived }, headers: authHeaders() }),
        axios.get(`${API}/admin/contact-messages`, { headers: authHeaders() }),
      ]);
      setItems(inb.data || []);
      setContactMsgs(ct.data || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const archive = async (id, val = true) => {
    try {
      await axios.patch(`${API}/admin/inbox/${id}`, { archived: val }, { headers: authHeaders() });
      toast.success(val ? "Archived" : "Restored");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await axios.delete(`${API}/admin/inbox/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="admin-inbox" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl text-white">Inbox</h3>
          <button
            data-testid="inbox-toggle-archived"
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs tracking-[0.2em] uppercase text-[#95A89C] hover:text-white"
          >
            {showArchived ? "Active" : "Archived"}
          </button>
        </div>
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {items.length === 0 && (
            <div className="p-6 border border-dashed border-[#1A2B20] text-sm text-[#95A89C] text-center">
              {showArchived ? "No archived messages." : "No inbound emails yet."}
            </div>
          )}
          {items.map((m) => (
            <button
              key={m.id}
              data-testid={`inbox-item-${m.id}`}
              onClick={() => setSelected(m)}
              className={`w-full text-left p-4 border ${
                selected?.id === m.id ? "border-[#279153] bg-[#0d1a13]" : "border-[#1A2B20] bg-[#050605]"
              } hover:border-[#279153] transition-colors`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-white text-sm truncate flex-1">{m.from || "(unknown)"}</div>
                <div className="text-[10px] text-[#5C7064] flex-shrink-0">{new Date(m.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-sm text-[#95A89C] truncate">{m.subject || "(no subject)"}</div>
            </button>
          ))}
        </div>

        {contactMsgs.length > 0 && (
          <>
            <h3 className="font-serif-display text-xl text-white mt-6">Contact form</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
              {contactMsgs.map((m) => (
                <div key={m.id} data-testid={`contact-msg-${m.id}`} className="p-3 bg-[#050605] border border-[#1A2B20]">
                  <div className="flex justify-between gap-2">
                    <div className="text-white text-sm">{m.name}</div>
                    <div className="text-[10px] text-[#5C7064]">{new Date(m.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-xs text-[#95A89C] mb-1">{m.email}</div>
                  <div className="text-sm text-[#95A89C] line-clamp-3 whitespace-pre-wrap">{m.message}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="lg:col-span-3">
        {selected ? (
          <div className="p-6 bg-[#09120C] border border-[#1A2B20]" data-testid="inbox-detail">
            <div className="flex items-start justify-between gap-3 mb-5 pb-5 border-b border-[#1A2B20]">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">From</div>
                <div className="text-white text-sm truncate">{selected.from}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mt-2">Subject</div>
                <div className="font-serif-display text-xl text-white">{selected.subject || "(no subject)"}</div>
                <div className="text-xs text-[#5C7064] mt-1">{new Date(selected.created_at).toLocaleString()}</div>
                <div className="flex gap-3 text-[10px] tracking-[0.3em] uppercase mt-2">
                  {selected.forwarded && <span className="text-[#279153]">↗ forwarded</span>}
                  {selected.auto_replied && <span className="text-[#279153]">↩ auto-replied</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="inbox-archive-btn"
                  onClick={() => archive(selected.id, !selected.archived)}
                  className="px-3 py-2 border border-white/20 text-white text-[10px] tracking-[0.3em] uppercase hover:border-[#279153] hover:text-[#279153]"
                >
                  {selected.archived ? "Restore" : "Archive"}
                </button>
                <button
                  data-testid="inbox-delete-btn"
                  onClick={() => remove(selected.id)}
                  className="p-2 text-[#95A89C] hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {selected.html ? (
              <div className="prose prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: selected.html }} />
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-white/90 font-body">{selected.text || "(empty body)"}</pre>
            )}
          </div>
        ) : (
          <div className="p-16 bg-[#09120C] border border-dashed border-[#1A2B20] text-center text-sm text-[#95A89C]">
            Select a message to read.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState({ total_revenue: 0, total_orders: 0, total_products: 0, pending_orders: 0 });
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [albums, setAlbums] = useState([]);

  const verify = async () => {
    try {
      await axios.get(`${API}/admin/verify`, { headers: authHeaders() });
      setAuthed(true);
    } catch {
      clearToken();
      setAuthed(false);
    }
  };

  const loadAll = async () => {
    try {
      const [pr, sa, st, al] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/sales`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/albums`),
      ]);
      setProducts(pr.data);
      setSales(sa.data);
      setStats(st.data);
      setAlbums(al.data);
    } catch {}
  };

  useEffect(() => { if (getToken()) verify(); }, []);
  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const logout = () => { clearToken(); setAuthed(false); };

  const resendThanks = async (sessionId) => {
    try {
      await axios.post(`${API}/admin/resend-thanks/${sessionId}`, {}, { headers: authHeaders() });
      toast.success("Thank-you email sent");
    } catch { toast.error("Could not send email"); }
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div data-testid="admin-page" className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex items-center justify-between border-b border-[#1A2B20] pb-8 mb-10">
        <div>
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Studio · Control room</div>
          <h1 className="font-serif-display text-4xl text-white">Grá admin</h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-xs tracking-[0.3em] uppercase hover:border-red-400 hover:text-red-400"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <StatBox label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} icon={DollarSign} testid="stat-revenue" />
        <StatBox label="Paid orders" value={stats.total_orders} icon={ShoppingBag} testid="stat-orders" />
        <StatBox label="Pieces" value={stats.total_products} icon={Package} testid="stat-products" />
        <StatBox label="Pending" value={stats.pending_orders} icon={Mail} testid="stat-pending" />
      </div>

      <div className="flex gap-2 mb-8 border-b border-[#1A2B20]">
        {[
          { id: "products", label: "Catalogue" },
          { id: "albums", label: "Albums" },
          { id: "sales", label: "Sales" },
          { id: "inbox", label: "Inbox" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-xs tracking-[0.3em] uppercase transition-colors ${
              tab === t.id ? "text-white border-b-2 border-[#279153]" : "text-[#95A89C] hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="grid lg:grid-cols-2 gap-8">
          <ProductForm albums={albums} onCreated={loadAll} />
          <div>
            <h3 className="font-serif-display text-2xl text-white mb-4">Catalogue</h3>
            <div className="space-y-2">
              {products.map((p) => <ProductRow key={p.id} product={p} albums={albums} onChanged={loadAll} />)}
              {products.length === 0 && <div className="text-sm text-[#95A89C]">No pieces yet.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "albums" && <AlbumsPanel albums={albums} onChanged={loadAll} />}

      {tab === "sales" && (
        <div>
          <h3 className="font-serif-display text-2xl text-white mb-4">Sales log</h3>
          <SalesTable sales={sales} onResend={resendThanks} />
        </div>
      )}

      {tab === "inbox" && <InboxPanel />}
    </div>
  );
}
